import {
  booleanAttribute,
  Component,
  Directive,
  ElementRef,
  inject,
  input,
  linkedSignal,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';

/**
 * Makes a section collapsible. The first child element of the host is treated as the
 * section header: it stays visible and clicking anywhere on it toggles the section
 * (except on other interactive elements inside it). All the other children are hidden
 * while collapsed (via the global `.section-collapsed` style), so their state is preserved.
 */
@Directive({
  selector: '[appCollapsibleSection]',
  exportAs: 'appCollapsibleSection',
  host: {
    class: 'collapsible-section',
    '[class.section-collapsed]': 'collapsed()',
    '(click)': 'onHostClick($event)',
  },
})
export class CollapsibleSectionDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Sections start collapsed unless `sectionExpanded` is set. */
  readonly sectionExpanded = input(false, { transform: booleanAttribute });
  readonly collapsed = linkedSignal(() => !this.sectionExpanded());

  toggle(): void {
    this.collapsed.update((value) => !value);
  }

  onHostClick(event: MouseEvent): void {
    const header = this.host.nativeElement.firstElementChild;
    const target = event.target as Element | null;
    if (!header || !target || !header.contains(target)) return;

    const isToggleButton = !!target.closest('.section-collapse-toggle');
    if (!isToggleButton) {
      const interactive = target.closest(INTERACTIVE_SELECTOR);
      if (interactive && header.contains(interactive)) return;
      // Don't toggle when the user is selecting header text.
      if (window.getSelection()?.toString()) return;
    }
    this.toggle();
  }
}

@Component({
  selector: 'app-section-collapse-toggle',
  imports: [ButtonModule],
  host: { class: 'section-collapse-toggle' },
  template: `
    <button
      pButton
      type="button"
      class="p-button-text p-button-rounded p-button-plain"
      [icon]="section.collapsed() ? 'pi pi-chevron-down' : 'pi pi-chevron-up'"
      [attr.aria-expanded]="!section.collapsed()"
      [attr.aria-label]="buttonLabel()"
      [title]="buttonLabel()"
    ></button>
  `,
})
export class SectionCollapseToggleComponent {
  readonly section = inject(CollapsibleSectionDirective);
  readonly sectionName = input<string>('');

  buttonLabel(): string {
    const action = this.section.collapsed() ? 'Espandi' : 'Comprimi';
    return this.sectionName() ? `${action} sezione ${this.sectionName()}` : `${action} sezione`;
  }
}
